import { getVaultMetadataByAddress } from '@/constants/nucleus-vaults';
import { useVaultPortfolioData } from '@/hooks/useVaultPortfolioData';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { makeRoycoMarketInteractionRequest } from '@/network/api';
import {
  getDepositExchangeRate,
  isDepositSpendApproved,
  prepareDepositTransactionData,
} from '@molecularlabs/nucleus-frontend';
import { TokenKey } from '@molecularlabs/nucleus-frontend/dist/vaults/tokens';
import { useQuery } from '@tanstack/react-query';
import { simulateContract, waitForTransactionReceipt } from '@wagmi/core';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { RawTxOption } from 'royco/api';
import { RecipeMarketHubAbi } from 'royco/constants';
import { erc20Abi } from 'viem';
import { parseUnits } from 'viem/utils';
import { useConfig, useWriteContract } from 'wagmi';
import { TokenOption } from '../../types';
import { useCurrentVault } from './useCurrentVault';
import { useDepositsAndWithdrawalsToasts } from './useDepositsAndWIthdrawalsToasts';

const isSplitTxObject = (
  data: unknown,
): data is {
  roycoApproveTxData?: RawTxOption;
  roycoDepositTxData?: RawTxOption;
} => {
  return typeof data === 'object' && data !== null && 'roycoApproveTxData' in data && 'roycoDepositTxData' in data;
};

export const useDeposits = ({ amount, depositToken }: { amount: number; depositToken: TokenOption | undefined }) => {
  const wagmiConfig = useConfig();

  const [isPendingDepositReceipt, setIsPendingDepositReceipt] = useState(false);
  const [isPendingRoycoDepositReceipt, setIsPendingRoycoDepositReceipt] = useState(false);
  const [searchParams] = useSearchParams();
  const roycoOnly = searchParams.get('royco_only') === 'true';
  const { address, chainId, vaultKey, marketId, vault, vaultAddress } = useCurrentVault();
  const { refetchVaultPortfolioData } = useVaultPortfolioData();
  const {
    writeContractAsync: writeApproveContractAsync,
    data: approvalTxHash,
    isPending: isPendingApproval,
    isError: isApprovalError,
  } = useWriteContract();
  const {
    writeContractAsync: writeApproveRoycoContractAsync,
    data: roycoApprovalTxHash,
    isPending: isPendingRoycoApproval,
    isError: isRoycoApprovalError,
  } = useWriteContract();
  const { getSuccessToast, getErrorToast } = useDepositsAndWithdrawalsToasts();

  const { data: depositSpendApprovedData, isLoading: isDepositedSpendApprovedLoading } = useQuery({
    queryKey: [address, depositToken, chainId],
    queryFn: async () => {
      if (!address || !depositToken) return;
      return isDepositSpendApproved({
        vaultKey,
        sourceChainId: chainId,
        userAddress: address,
        depositTokenSymbol: depositToken.symbol,
      });
    },
  });

  const vaultDepositSpendAllowance = Number(depositSpendApprovedData?.allowance ?? 0);

  const { writeContractAsync: writeDepositContractAsync, data: depositTxHash } = useWriteContract();
  const { writeContractAsync: writeRoycoDepositContractAsync, data: roycoDepositTxHash } = useWriteContract();

  const { data: exchangeRate = 0 } = useQuery({
    queryKey: ['getDepositExchangeRate', depositToken, vaultKey],
    queryFn: async () => {
      if (!chainId || !depositToken) return 0;
      // if deposit token === vaultToken, then this means this is a royco deposit.
      // yes, this probably needs to be refactored
      if (depositToken.symbol === vault?.token.symbol) return 1;
      try {
        const exchangeRateResponse = await getDepositExchangeRate({
          vaultKey,
          sourceChainId: chainId,
          depositTokenSymbol: depositToken?.symbol,
          minimumFractionDigits: 2,
        });
        if (typeof exchangeRateResponse === 'string') return Number(exchangeRateResponse);
        if (exchangeRateResponse === undefined) return 0;
        throw exchangeRateResponse;
      } catch (e) {
        console.error(e);
        return 0;
      }
    },
    enabled: Boolean(chainId),
  });
  // if there's no exchangeRate, that means that the chosen token here is the vaultToken
  const vaultTokenAmount = depositToken?.address === vault?.token.address ? amount : amount * (exchangeRate ?? 0);
  const vaultTokenAmountBn = parseUnits(vaultTokenAmount?.toString() ?? '', vault?.token.decimals ?? 0);

  const { data: txData, isLoading: isLoadingVaultApprovalData } = useQuery({
    queryKey: ['prepareDepositTransactionData', vaultKey, amount, address, depositToken?.symbol, chainId],
    queryFn: async () => {
      if (!depositToken) return;
      const prepareDepositDataParams = {
        vaultKey: vaultKey,
        userAddress: address as EthAddress,
        depositTokenSymbol: depositToken.symbol as TokenKey,
        chainId: chainId,
        depositAmount: amount.toString(),
        slippage: 10000,
      };
      const depositData = await prepareDepositTransactionData(prepareDepositDataParams);
      return depositData;
    },
    enabled: !roycoOnly,
  });

  const { data: roycoTxData, isLoading: isLoadingRoycoApprovalData } = useQuery({
    queryKey: ['roycoDepositTxData', address, chainId, marketId, amount],
    queryFn: async () => {
      if (!chainId || !address || !vaultTokenAmountBn || !marketId) return;
      try {
        const roycoMarketInteractionData = await makeRoycoMarketInteractionRequest({
          chainId: chainId.toString(),
          marketId,
          amount: vaultTokenAmountBn.toString(),
          accountAddress: address,
        });

        const rawTxOptions = roycoMarketInteractionData?.rawTxOptions;
        if (Array.isArray(rawTxOptions)) {
          return {
            roycoApproveTxData: rawTxOptions.find((tx) => tx.functionName === 'approve'),
            roycoDepositTxData: rawTxOptions.find((tx) => tx.functionName === 'fillIPOffers'),
          } satisfies {
            roycoApproveTxData: RawTxOption | undefined;
            roycoDepositTxData: RawTxOption | undefined;
          };
        }
        return roycoMarketInteractionData;
      } catch (e) {
        console.error('Error retrieving roycoDepositTxData', e);
        throw e;
      }
    },
    enabled: Boolean(vaultTokenAmountBn && address && chainId),
  });
  const { roycoApproveTxData, roycoDepositTxData } = isSplitTxObject(roycoTxData) ? roycoTxData : {};

  const approveVaultSpending = async () => {
    if (!txData) {
      return;
    }
    await simulateContract(wagmiConfig, {
      abi: erc20Abi,
      address: txData.args[0],
      args: [vaultAddress as EthAddress, txData.args[1]],
      functionName: 'approve',
    });
    await writeApproveContractAsync({
      abi: erc20Abi,
      address: txData.args[0],
      args: [vaultAddress as EthAddress, txData.args[1]],
      functionName: 'approve',
    });
  };

  const approveRoycoSpending = async () => {
    if (!roycoApproveTxData) return;
    await simulateContract(wagmiConfig, {
      abi: erc20Abi,
      address: roycoApproveTxData.address as EthAddress,
      functionName: roycoApproveTxData.functionName as 'approve',
      args: roycoApproveTxData.args as [`0x${string}`, bigint],
    });
    await writeApproveRoycoContractAsync({
      abi: erc20Abi,
      address: roycoApproveTxData.address as EthAddress,
      functionName: roycoApproveTxData.functionName as 'approve',
      args: roycoApproveTxData.args as [`0x${string}`, bigint],
    });
  };

  const depositIntoVault = async () => {
    if (!txData) return;
    try {
      setIsPendingDepositReceipt(true);
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerDepositAttempt, {
        vault_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
        deposit_amount: String(amount),
      });
      await simulateContract(wagmiConfig, txData);
      const hash = await writeDepositContractAsync(txData);
      await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerDeposit, {
        vault_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
        deposit_amount: String(amount),
      });
      await refetchVaultPortfolioData();
      getSuccessToast({
        actionName: 'vault deposit',
        destinationName: 'vault balance',
      });
    } catch (e) {
      console.error(e);
      getErrorToast({
        actionName: 'vault deposit',
        errorMessage: String(e),
      });
    } finally {
      setIsPendingDepositReceipt(false);
    }
  };

  const depositIntoRoyco = async () => {
    if (!roycoDepositTxData) return;
    try {
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerRoycoDepositAttempt, {
        market_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
        deposit_amount: String(amount),
      });
      setIsPendingRoycoDepositReceipt(true);
      const txData = {
        abi: RecipeMarketHubAbi,
        address: roycoDepositTxData.address as EthAddress,
        args: roycoDepositTxData.args as [],
        functionName: roycoDepositTxData.functionName,
      };
      // Issue with royco sdk types
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      await simulateContract(config, txData);
      // Issue with royco sdk types
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      const hash = await writeRoycoDepositContractAsync(txData);
      await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalOptimizerRoycoDeposit, {
        market_name: getVaultMetadataByAddress(vaultAddress)?.name || vaultAddress,
        deposit_amount: String(amount),
      });
      getSuccessToast({
        actionName: 'royco deposit',
        destinationName: 'royco balance',
      });
    } catch (e) {
      console.error(e);
      getErrorToast({
        actionName: 'royco deposit',
        errorMessage: String(e),
      });
    } finally {
      setIsPendingRoycoDepositReceipt(false);
    }
  };
  return {
    approveVaultSpending,
    approveRoycoSpending,
    isLoadingVaultApprovalData,
    isLoadingRoycoApprovalData,
    vaultSpendingApproved: Boolean(approvalTxHash) || vaultDepositSpendAllowance >= amount,
    roycoSpendingApproved: Boolean(roycoApproveTxData?.txStatus === 'success') || Boolean(roycoApprovalTxHash),
    isPendingApproval: isPendingApproval && !isApprovalError,
    isPendingRoycoApproval: isPendingRoycoApproval && !isRoycoApprovalError,
    approvalTxHash,
    roycoApprovalTxHash,
    depositIntoRoyco,
    depositIntoVault,
    depositTxHash,
    roycoDepositTxHash,
    isPendingDepositReceipt,
    isPendingRoycoDepositReceipt,
    isDepositedSpendApprovedLoading,
    isApprovalError,
    isRoycoApprovalError,
    exchangeRate,
  };
};
